// PWA do Agzos Office (B29-M1): registro do service worker, aviso de versão nova, botão
// Instalar (quando o navegador oferece), dica "Adicionar à Tela de Início" no iOS e aviso de
// sem conexão com o servidor.
//
// Os avisos aparecem só quando há o que dizer. Sem nada a avisar, a tela fica igual ao desktop.

import { onLangChange, t, type Chave } from './i18n.js';

interface EventoInstalar extends Event {
  prompt(): Promise<void>;
}

/** Chave do localStorage: a pessoa fechou a dica do iOS. */
const CHAVE_DICA_IOS = 'agzos.pwa.dicaIos';
const INTERVALO_CONEXAO_MS = 15_000;

/** iPhone, iPod e iPad (o iPadOS 13+ se diz Mac, mas tem tela de toque). */
export function ehIos(ua: string, pontosDeToque: number): boolean {
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && pontosDeToque > 1);
}

/** A dica vale no iOS fora do app instalado: o Safari não tem o botão Instalar. */
export function precisaDicaIos(ua: string, pontosDeToque: number, instalado: boolean, dispensada: boolean): boolean {
  return !instalado && !dispensada && ehIos(ua, pontosDeToque);
}

interface Estado {
  instalar?: EventoInstalar;
  /** Versão nova instalada e esperando: o botão Atualizar a ativa. */
  nova?: ServiceWorker;
  semConexao: boolean;
  dicaIos: boolean;
}

const estado: Estado = { semConexao: false, dicaIos: false };
let caixa: HTMLElement;
let pedidoAtualizar = false;
let tentativa: number | undefined;

function avisoHtml(acao: string, texto: Chave, botao: Chave, fechar?: Chave): string {
  const extra = fechar ? `<button type="button" data-acao="${acao}-fechar">${t(fechar)}</button>` : '';
  return `<div class="pwa-aviso" role="status"><p>${t(texto)}</p><div class="pwa-acoes">${extra}<button type="button" data-acao="${acao}">${t(botao)}</button></div></div>`;
}

function pinta(): void {
  const itens: string[] = [];
  if (estado.nova) itens.push(avisoHtml('atualizar', 'pwa.nova_versao', 'pwa.atualizar'));
  if (estado.semConexao) itens.push(avisoHtml('tentar', 'pwa.sem_conexao', 'pwa.tentar'));
  if (estado.instalar) itens.push(avisoHtml('instalar', 'pwa.instalar_texto', 'pwa.instalar', 'pwa.agora_nao'));
  if (estado.dicaIos) {
    itens.push(`<div class="pwa-aviso" role="status"><p>${t('pwa.ios')}</p><div class="pwa-acoes"><button type="button" data-acao="ios-fechar">${t('pwa.agora_nao')}</button></div></div>`);
  }
  caixa.innerHTML = itens.join('');
}

async function acao(alvo: string): Promise<void> {
  if (alvo === 'atualizar') {
    // a página recarrega sozinha quando o controle trocar (ver registraSw)
    pedidoAtualizar = true;
    estado.nova?.postMessage({ type: 'SKIP_WAITING' });
  } else if (alvo === 'tentar') {
    location.reload();
  } else if (alvo === 'instalar') {
    const evento = estado.instalar;
    estado.instalar = undefined;
    pinta();
    await evento?.prompt().catch(() => undefined);
  } else if (alvo === 'instalar-fechar') {
    estado.instalar = undefined;
    pinta();
  } else if (alvo === 'ios-fechar') {
    try {
      localStorage.setItem(CHAVE_DICA_IOS, '1');
    } catch {
      /* navegador sem localStorage: a dica volta na próxima vez, sem problema */
    }
    estado.dicaIos = false;
    pinta();
  }
}

/** Mesmo com a página aberta, tenta o servidor de novo a cada 15 s enquanto estiver fora do ar. */
async function vigiaConexao(): Promise<void> {
  clearTimeout(tentativa);
  const ok = await fetch('/api/health', { cache: 'no-store' }).then(
    (r) => r.ok,
    () => false,
  );
  estado.semConexao = !ok;
  pinta();
  if (!ok) tentativa = window.setTimeout(() => void vigiaConexao(), INTERVALO_CONEXAO_MS);
}

async function registraSw(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  const reg = await navigator.serviceWorker.register('/sw.js');
  // a primeira instalação não tem versão antiga para trocar: não avisa
  const temControle = (): boolean => !!navigator.serviceWorker.controller;
  const avisaNova = (novo: ServiceWorker): void => {
    if (!temControle()) return;
    estado.nova = novo;
    pinta();
  };
  if (reg.waiting) avisaNova(reg.waiting);
  reg.addEventListener('updatefound', () => {
    const novo = reg.installing;
    novo?.addEventListener('statechange', () => {
      if (novo.state === 'installed') avisaNova(novo);
    });
  });
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (pedidoAtualizar) location.reload();
  });
  // ao voltar para a aba, procura versão nova (o navegador só checa sozinho de tempos em tempos)
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) void reg.update().catch(() => undefined);
  });
}

/** Monta os avisos do PWA. Chamado uma vez, na abertura da página do escritório. */
export function montaPwa(): void {
  caixa = document.createElement('div');
  caixa.id = 'pwa';
  caixa.addEventListener('click', (ev) => {
    const alvo = (ev.target as HTMLElement).closest<HTMLElement>('[data-acao]')?.dataset.acao;
    if (alvo) void acao(alvo);
  });
  document.body.append(caixa);

  // iOS marca o app instalado em navigator.standalone (não tem display-mode)
  const instalado = matchMedia('(display-mode: standalone)').matches || (navigator as unknown as { standalone?: boolean }).standalone === true;
  let dispensada = false;
  try {
    dispensada = localStorage.getItem(CHAVE_DICA_IOS) === '1';
  } catch {
    /* sem localStorage: a dica aparece, e tudo bem */
  }
  estado.dicaIos = precisaDicaIos(navigator.userAgent, navigator.maxTouchPoints, instalado, dispensada);

  // o Chrome e o Edge oferecem o app instalável por este evento: guardado até o botão Instalar
  window.addEventListener('beforeinstallprompt', (ev: Event) => {
    ev.preventDefault();
    estado.instalar = ev as EventoInstalar;
    pinta();
  });
  window.addEventListener('appinstalled', () => {
    estado.instalar = undefined;
    pinta();
  });

  window.addEventListener('offline', () => {
    estado.semConexao = true;
    pinta();
  });
  window.addEventListener('online', () => void vigiaConexao());

  onLangChange(() => pinta());
  pinta();
  void registraSw().catch((err: unknown) => console.warn('service worker não registrou', err));
  void vigiaConexao();
}

// ------------------------------------------------------------------ notificações no celular (B29-M5)

/** Quais avisos este aparelho quer: "precisa de você" e "concluiu". */
export interface QuaisPush {
  precisa: boolean;
  concluiu: boolean;
}

export type ResultadoPush = { ok: true } | { ok: false; erro: 'sem-suporte' | 'negada' | 'falhou' };

/** O navegador faz Web Push? (iOS só dentro do app instalado na Tela de Início, a partir do iOS 16.4.) */
export function pushSuportado(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/** Base64url (chave VAPID do servidor) em bytes, do jeito que o pushManager pede. */
function bytesDaChave(texto: string): Uint8Array<ArrayBuffer> {
  const bin = atob(texto.replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}

async function enviaJson(caminho: string, corpo: unknown): Promise<{ ok: boolean; status: number }> {
  const res = await fetch(caminho, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(corpo) });
  return { ok: res.ok, status: res.status };
}

/** Inscreve este aparelho (pede a permissão, se faltar) e guarda as preferências no servidor. */
export async function inscreverPush(quais: QuaisPush): Promise<ResultadoPush> {
  if (!pushSuportado()) return { ok: false, erro: 'sem-suporte' };
  if ((await Notification.requestPermission()) !== 'granted') return { ok: false, erro: 'negada' };
  try {
    const reg = await navigator.serviceWorker.ready;
    const chave = (await (await fetch('/api/push/chave', { cache: 'no-store' })).json()) as { chave: string };
    const sub =
      (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: bytesDaChave(chave.chave) }));
    const res = await enviaJson('/api/push/inscrever', { subscription: sub.toJSON(), quais });
    return res.ok ? { ok: true } : { ok: false, erro: 'falhou' };
  } catch {
    return { ok: false, erro: 'falhou' };
  }
}

/** Troca só as preferências de um aparelho já inscrito (sem pedir permissão de novo). */
export async function atualizaQuaisPush(quais: QuaisPush): Promise<boolean> {
  if (!pushSuportado()) return false;
  const sub = await (await navigator.serviceWorker.ready).pushManager.getSubscription();
  if (!sub) return false;
  return (await enviaJson('/api/push/inscrever', { subscription: sub.toJSON(), quais })).ok;
}

/** Cancela a inscrição deste aparelho no servidor e no navegador. */
export async function cancelarPush(): Promise<boolean> {
  if (!pushSuportado()) return false;
  const sub = await (await navigator.serviceWorker.ready).pushManager.getSubscription();
  if (!sub) return true;
  await enviaJson('/api/push/cancelar', { endpoint: sub.endpoint }).catch(() => undefined);
  return sub.unsubscribe();
}

/** Este aparelho já está inscrito? */
export async function inscritoPush(): Promise<boolean> {
  if (!pushSuportado()) return false;
  return !!(await (await navigator.serviceWorker.ready).pushManager.getSubscription());
}
